import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { type Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';

import { type PagedUser, type User } from './user.model';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);

  private readonly apiUrl = `${environment.apiBaseUrl}/user`;

  createUser(user: User): Observable<string> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.post(`${this.apiUrl}/create`, user, {
      headers,
      responseType: 'text'
    });
  }

  updateUser(user: User): Observable<string> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.put(`${this.apiUrl}/update`, user, {
      headers,
      responseType: 'text'
    });
  }

  updateProfile(user: User): Observable<string> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.put(`${this.apiUrl}/update-profile`, user, {
      headers,
      responseType: 'text'
    });
  }

  getUserById(userId: string): Observable<User> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.get<User>(`${this.apiUrl}/get/${userId}`, { headers });
  }

  getLoggedInUserProfile(): Observable<User> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.get<any>(`${environment.apiBaseUrl}/loggedInUser/profile`, { headers });
  }

  deleteUser(userId: string): Observable<void> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.delete<void>(`${this.apiUrl}/hide/${userId}`, { headers });
  }

  getPagedUsers(page: number, size: number, active: string, name: string, email: string, roleId: string, sort: string[]): Observable<PagedUser> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('active', active !== undefined ? active : 'all');

    if (name) {
      params = params.set('name', name.trim());
    }
    if (email) {
      params = params.set('email', email.trim());
    }
    if (roleId) {
      params = params.set('roleId', roleId);
    }
    if (sort && sort.length) {
      sort.forEach(sortItem => {
        params = params.append('sort', sortItem);
      });
    }

    return this.http.get<PagedUser>(`${this.apiUrl}/find-all-paged`, { params: params });
  }

  getUsersForDropdown(active: string): Observable<User[]> {
    return this.http.get<User[]>(`${this.apiUrl}/find-all-for-dropdown/${active}`);
  }

  getUserLabel(user: User): string {
    return user ? (user.active ? `${user.fullName}` : `${user.fullName} (${this.translate.instant('COMMON.INACTIVE')})`) : '';
  }

  uploadImages(id: string, formData: FormData): Observable<any> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.put(`${this.apiUrl}/uploadImages/${id}`, formData, { headers });
  }

  downloadImages(params: string): Observable<Blob> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.get(`${this.apiUrl}/downloadImages/${params}`, {
      responseType: 'blob', // Ensure the response is a Blob (binary data)
      headers
    });
  }

  deleteImages(params: string): Observable<Blob> {
    const headers = new HttpHeaders().set('X-Spinner', 'true');
    return this.http.delete(`${this.apiUrl}/deleteImages/${params}`, {
      responseType: 'blob', // Ensure the response is a Blob (binary data)
      headers
    });
  }
}
